/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { M365CopilotBenefitActionTypes } from "Dataflows/M365CopilotBenefit/M365CopilotBenefitAction";
import { IM365CopilotBenefit } from "Interfaces/Benefits/IM365CopilotBenefit";
import { formatDate } from "Utility/FormatHelper";

import { IM365CopilotBenefitListProps } from "./IM365CopilotBenefitListProps";

/**
 * M365CopilotBenefitList component
 * @param props - the props for the component
 * @returns the element
 */
const M365CopilotBenefitList = (props: IM365CopilotBenefitListProps): ReactElement => {
  const {
    data,
    loadM365CopilotBenefitError,
    isLoadingM365CopilotBenefit,
    filteredCount,
    currentPage,
    pageSize,
    actions: { updateCurrentPage, updatePageSize },
  } = props;

  const formatDisplayDate = (incomingDate: Date | undefined): React.ReactElement => {
    if (incomingDate) {
      return <span>{formatDate(incomingDate)}</span>;
    } else {
      return <></>;
    }
  };

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={statusKey} />;
  };

  const columns: IColumn[] = [
    {
      key: "userId",
      name: "User ID",
      ariaLabel: "User ID",
      fieldName: "userId",
      minWidth: 70,
      isResizable: true,
    },
    {
      key: "copilotTenantId",
      name: "Tenant Id",
      ariaLabel: "The copilot tenant id",
      fieldName: "copilotTenantId",
      minWidth: 240,
      isResizable: true,
    },
    {
      key: "claimedDate",
      name: "Claimed Date",
      ariaLabel: "Date user claim copilot tenant successfully",
      fieldName: "claimedDate",
      minWidth: 150,
      onRender: (item: IM365CopilotBenefit): React.ReactElement => {
        return formatDisplayDate(item.claimedDate);
      },
      isResizable: true,
    },
    {
      key: "claimedBy",
      name: "Claimed by (User Email)",
      ariaLabel: "Claimed by (User Email)",
      fieldName: "claimedBy",
      minWidth: 180,
      isResizable: true,
    },
    {
      key: "assignedTo",
      name: "Assigned to (User Email)",
      ariaLabel: "Assigned to (User Email)",
      fieldName: "assignedTo",
      minWidth: 180,
      isResizable: true,
    },
    {
      key: "awardPeriod",
      name: "Award Period",
      ariaLabel: "Name of Award Period code was claimed during",
      fieldName: "awardPeriod",
      minWidth: 160,
      isResizable: true,
    },
  ];

  if (isLoadingM365CopilotBenefit) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadM365CopilotBenefitError !== "") {
    return <div>{loadM365CopilotBenefitError}</div>;
  }

  return (
    <>
      <DetailsList
        items={data}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: IM365CopilotBenefit): string => `${item.userId}${item.copilotTenantId}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page): M365CopilotBenefitActionTypes => updateCurrentPage(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default M365CopilotBenefitList;
